import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  Collapsible,
  Divider,
  InlineGrid,
  InlineStack,
  Link,
  RadioButton,
  Select,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris'
import { ChevronDownIcon, ChevronUpIcon, SettingsIcon } from '@shopify/polaris-icons'
import { memo, useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import debounce from 'lodash.debounce'

import ProductTagList from '../../../../components/ProductsList'
import RecommendationSettingsModal from '../../../../components/RecommendationSettingModal'
import SlotRulesModal from '../../../Recommendation/SlotRules/SlotRulesModal'
import {
  AJAX_CART,
  CART,
  CHECKOUT,
  COLLECTION,
  ORDER_CONFIRM,
  PRODUCT,
} from '../../../../constants/page'
import * as WIDGETS from '../../../../constants/widgets'
import {
  CUSTOM_RECOMMENDATION_LOGIC,
  RECOMMENDATIONS_RANKING_CRITERIA
} from '../../../../constants/recommendations'
import CollectionSelection from '../components/CollectionSelection'
import EnabledUrls from '../components/EnabledUrls'
import ObjectRecommendation from '../components/ObjectRecommendation'
import RecommendationSettingCard from '../../components/RecommendationSettingCard'
import SlotsRecommendation from '../components/SlotsRecommendation'
import { areFeaturesEnabled } from '../../../../utils/features'
import { FEATURES } from '../../../../constants/features'
import { useGlobalRecommendations } from '../../../../hooks/useGlobalRecommendations'

export default function Recommendation({
  form,
  sectionId,
  dashboardData,
  pricing,
  section,
  page: sectionPage
}) {
  const { t } = useTranslation()

  const [openedSetting, setOpenedSetting] = useState(null)
  const [advanceConfigCollapsed, setAdvancedConfigCollapsed] = useState(true)
  const [slotRulesModalOpen, setSlotRulesModalOpen] = useState(false)
  


  const [disabledTags, setDisabledTags] = [
    form.fields.disabledTags.value,
    form.fields.disabledTags.onChange,
  ]

  const [excludedTags, setExcludedTags] = [
    form.fields.excludedTags.value,
    form.fields.excludedTags.onChange,
  ]

  const [whitelistedTags, setWhitelistedTags] = [
    form.fields.whitelistedTags.value,
    form.fields.whitelistedTags.onChange,
  ]

  const [filterTags, setFilterTags] = [
    form.fields.filterTags.value,
    form.fields.filterTags.onChange,
  ]

  const [disabledProducts, setDisabledProducts] = [
    form.fields.disabledProducts.value,
    (value) => {
      form.fields.disabledProducts.onChange(value ?? [])
    },
  ]

  const [excludedProducts, setExcludedProducts] = [
    form.fields.excludedProducts.value,
    (value) => {
      form.fields.excludedProducts.onChange(value ?? [])
    },
  ]

  const [whitelistedProducts, setWhitelistedProducts] = [
    form.fields.whitelistedProducts.value,
    (value) => {
      form.fields.whitelistedProducts.onChange(value ?? [])
    },
  ]

  const supportedSettings = Object.values(WIDGETS).find(
    ({ value }) => value === form.fields.widgetType.value,
  ).supportedSettings

  const widgetType = form.fields.widgetType.value
  const page = form.fields.page.value
  
  const { count, isLoading } = useGlobalRecommendations(widgetType)


  const recommendationOptions = (() => {
    const arr = []
    if (supportedSettings.includes('automaticEnabled')) {
      arr.push({
        field: form.fields.automaticEnabled,
        slug: 'automaticEnabled',
        title: t('WidgetConfig.automaticEnabled.label'),
        description: t('WidgetConfig.automaticEnabled.description'),
      })
    }
    if (supportedSettings.includes('enableRandom')) {
      arr.push({
        field: form.fields.enableRandom,
        slug: 'enableRandom',
        title: t('WidgetConfig.enableRandom.label'),
        description: t('WidgetConfig.enableRandom.description'),
      })
    }
    if (supportedSettings.includes('allowIfUnavailable')) {
      arr.push({
        field: form.fields.allowIfUnavailable,
        slug: 'allowIfUnavailable',
        title: t('WidgetConfig.allowIfUnavailable.label'),
        description: t('WidgetConfig.allowIfUnavailable.description'),
      })
    }
    // Add collection filter for trending and bestsellers on collection pages
    if (
      (widgetType === WIDGETS.TRENDING.value || widgetType === WIDGETS.BESTSELLERS.value) &&
      page === COLLECTION.value
    ) {
      const recommendationType = t(`Widgets.${widgetType}.lowercaseLabel`)
      arr.push({
        field: {
          checked: form.fields.extra.value?.showFromCurrentCollection || false,
          onChange: (value) => {
            form.fields.extra.onChange({
              ...form.fields.extra.value,
              showFromCurrentCollection: value
            })
          }
        },
        slug: 'showFromCurrentCollection',
        title: t('WidgetConfig.showFromCurrentCollection.label', { recommendationType }),
        description: t('WidgetConfig.showFromCurrentCollection.description'),
      })
    }

    return arr
  })()

  const advancedSettingsVisible = Object.values(RecommendationsVisiblMap)
    .map((fn) => fn(widgetType, page))
    .some(Boolean)

  return (
    <Box>
      <BlockStack gap={'400'}>
        <Card padding={'0'}>
          <BlockStack>
            <Box id="recommendation-config">
              <BlockStack gap="200">
                <Box padding={'400'}>
                  <Text as="strong" variant="headingSm">
                    {t('Section.Edit.Sections.Recommendation.ConfigSelection.title')}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    {t('Section.Edit.Sections.Recommendation.ConfigSelection.description')}
                  </Text>
                </Box>
                <InlineGrid columns={'1fr'} gap={'200'}>
                  <BlockStack>
                    {ALL_SETTINGS.sort((a, b) => {
                      const isFirstEnabled = RecommendationsVisiblMap[a](widgetType, page)
                      const isSecondEnabled = RecommendationsVisiblMap[b](widgetType, page)
                      if (isFirstEnabled && !isSecondEnabled) {
                        return 1
                      }
                      if (!isFirstEnabled && isSecondEnabled) {
                        return -1
                      }
                      return 0
                    }).map((setting) => {
                      if (setting === 'globalRecommendation' && !section.extra.hasPinnedProducts && (isLoading || count == 0)) {
                        return null;
                      }
                      if (setting === 'slotRules' && !section?.extra?.enableSlotRules) {
                        return null;
                      }
                      const upgradeRequired =
                        setting === 'ruleBasedRecommendation' &&
                        !areFeaturesEnabled(
                          [FEATURES.RECOMMENDATION_RULES],
                          window.shopify.data,
                        )
                      return (
                        <RecommendationSettingCard
                          key={setting}
                          title={t(`Recommendation.${setting}.title`)}
                          description={t(`Recommendation.${setting}.description`)}
                          onClick={() => {
                            if (upgradeRequired) {
                              pricing.open({
                                features: [FEATURES.RECOMMENDATION_RULES],
                              })
                              return
                            }
                            if (setting === 'slotRules') {
                              setSlotRulesModalOpen(true)
                              return
                            }
                            setOpenedSetting(setting)
                          }}
                          btnText={t(`Recommendation.btnText`)}
                          disabled={!RecommendationsVisiblMap[setting](widgetType, page)}
                          upgradeRequired={upgradeRequired}
                        />
                      )
                    })}
                  </BlockStack>
                </InlineGrid>
              </BlockStack>
            </Box>
            {widgetType !== WIDGETS.SLOTS_WIDGET.value &&
              widgetType !== WIDGETS.PROMOTED_PRODUCTS.value &&
              widgetType !== WIDGETS.COLLECTIONS_WIDGET.value &&
              advancedSettingsVisible && (
                <Box>
                  <BlockStack gap={'400'}>
                    <Box padding={'200'}>
                      <Button
                        variant="plain"
                        icon={advanceConfigCollapsed ? ChevronDownIcon : ChevronUpIcon}
                        onClick={() => setAdvancedConfigCollapsed(!advanceConfigCollapsed)}
                      >
                        {t(`Button.${advanceConfigCollapsed ? 'show' : 'hide'}`, {
                          text: t('Section.Edit.Sections.AdvanceConfig.title'),
                        })}
                      </Button>
                    </Box>
                    <Collapsible open={!advanceConfigCollapsed}>
                      {recommendationOptions.map(({ title, description, slug, field }) => (
                        <Box
                          key={slug}
                          onClick={() => field.onChange(!field?.checked)}
                          borderBlockStartWidth="025"
                          borderColor="border"
                          overflow="hidden"
                          cursor="pointer"
                        >
                          <Box background="fill" height="100%">
                            <Box position="relative">
                              <Box
                                position="absolute"
                                insetBlockStart="button-group-gap"
                                insetInlineEnd="button-group-gap"
                                zIndex={1000}
                              >
                                <Checkbox {...field} checked={field?.checked} />
                              </Box>
                              <Box padding="card-padding" background="surface">
                                <Text as="strong">{title}</Text>
                                <Text as="p">{description}</Text>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                      {widgetType === WIDGETS.BESTSELLERS.value && page === COLLECTION.value && (
                        <Box
                          borderBlockStartWidth="025"
                          borderColor="border"
                          overflow="hidden"
                        >
                          <Box background="fill" height="100%">
                            <Box padding="card-padding" background="surface">
                              <BlockStack gap="200">
                                <Text as="strong">{t('WidgetConfig.bestsellerDays.label')}</Text>
                                <Text as="p">{t('WidgetConfig.bestsellerDays.description')}</Text>
                                <Select
                                  label=""
                                  options={[
                                    { label: t('WidgetConfig.bestsellerDays.options.7'), value: '7' },
                                    { label: t('WidgetConfig.bestsellerDays.options.15'), value: '15' },
                                    { label: t('WidgetConfig.bestsellerDays.options.30'), value: '30' }
                                  ]}
                                  value={String(form.fields.extra.value?.bestsellerDays || 30)}
                                  onChange={(value) => {
                                    form.fields.extra.onChange({
                                      ...form.fields.extra.value,
                                      bestsellerDays: parseInt(value, 10)
                                    })
                                  }}
                                  disabled={!form.fields.extra.value?.showFromCurrentCollection}
                                />
                              </BlockStack>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Collapsible>
                  </BlockStack>
                </Box>
            )}
          </BlockStack>
        </Card>
        {[
          WIDGETS.PERSONALIZED.value,
          WIDGETS.SIMILAR_PRODUCTS.value,
          WIDGETS.BOUGHT_TOGETHER.value,
          WIDGETS.CROSS_SELL.value,
        ].includes(widgetType) && (
          <>
            <ProductTagList
              id={'recommendation-excluded'}
              helpText={t('Section.Edit.Sections.Recommendation.ExcludedProduct.helpText', {
                link: (
                  <Link monochrome url="/recommendation?recSection=excluded-products">
                    this
                  </Link>
                ),
              })}
              productSelectionTitle={t('Section.Edit.Sections.Recommendation.ExcludedProduct.title')}
              productSelectionButtonCta={t('Section.Edit.Sections.Recommendation.ExcludedProduct.addProductCTA')}
              productSelectionDescription={t('Section.Edit.Sections.Recommendation.ExcludedProduct.description')}
              products={excludedProducts}
              onProductSelectionChange={setExcludedProducts}
              tagsTitle={t('Section.Edit.Sections.Recommendation.ExcludedProduct.tagsTitle')}
              tagsDescription={t('Section.Edit.Sections.Recommendation.ExcludedProduct.tagsDescription')}
              tags={excludedTags}
              onTagChange={setExcludedTags}
              queryPlaceholder={t('Section.Edit.Sections.Recommendation.ExcludedProduct.tagQueryPlaceholder')}
              tagBtnCta={t('Section.Edit.Sections.Recommendation.ExcludedProduct.addTagBtnText')}
            />
            {page === PRODUCT.value && (
              <>
                <ProductTagList
                  id={'recommendation-disabled'}
                  helpText={t('Section.Edit.Sections.Recommendation.DisabledProduct.helpText', {
                    link: (
                      <Link monochrome url="/recommendation?recSection=disabled-products">
                        this
                      </Link>
                    ),
                  })}
                  productSelectionTitle={t('Section.Edit.Sections.Recommendation.DisabledProduct.title')}
                  productSelectionButtonCta={t('Section.Edit.Sections.Recommendation.DisabledProduct.addProductCTA')}
                  productSelectionDescription={t('Section.Edit.Sections.Recommendation.DisabledProduct.description')}
                  products={disabledProducts}
                  onProductSelectionChange={setDisabledProducts}
                  tagsTitle={t('Section.Edit.Sections.Recommendation.DisabledProduct.tagsTitle')}
                  tagsDescription={t('Section.Edit.Sections.Recommendation.DisabledProduct.tagsDescription')}
                  tags={disabledTags}
                  onTagChange={setDisabledTags}
                  queryPlaceholder={t('Section.Edit.Sections.Recommendation.DisabledProduct.tagQueryPlaceholder')}
                  tagBtnCta={t('Section.Edit.Sections.Recommendation.DisabledProduct.addTagBtnText')}
                />
                <ProductTagList
                  id="recommendation-whitelisted"
                  helpText={t('Section.Edit.Sections.Recommendation.WhitelistedProduct.infoBanner')}
                  productSelectionTitle={t('Section.Edit.Sections.Recommendation.WhitelistedProduct.title')}
                  productSelectionButtonCta={t('Section.Edit.Sections.Recommendation.WhitelistedProduct.addProductCTA')}
                  productSelectionDescription={t('Section.Edit.Sections.Recommendation.WhitelistedProduct.description')}
                  products={whitelistedProducts}
                  onProductSelectionChange={setWhitelistedProducts}
                  tagsTitle={t('Section.Edit.Sections.Recommendation.WhitelistedProduct.tagsTitle')}
                  tagsDescription={t('Section.Edit.Sections.Recommendation.WhitelistedProduct.tagsDescription')}
                  tags={whitelistedTags}
                  onTagChange={setWhitelistedTags}
                  queryPlaceholder={t('Section.Edit.Sections.Recommendation.WhitelistedProduct.tagQueryPlaceholder')}
                  tagBtnCta={t('Section.Edit.Sections.Recommendation.WhitelistedProduct.addTagBtnText')}
                  maxProducts={100}
                />
              </>
            )}
          </>
        )}
        {widgetType === WIDGETS.PERSONALIZED.value && (
          <ProductTagList
            id="recommendation-additional-filters"
            tagsOnly={true}
            helpText={t('Section.Edit.Sections.Recommendation.AdditionalFilters.infoBanner')}
            tagsTitle={t('Section.Edit.Sections.Recommendation.AdditionalFilters.tagsTitle')}
            tagsDescription={t('Section.Edit.Sections.Recommendation.AdditionalFilters.tagsDescription')}
            tags={filterTags}
            onTagChange={setFilterTags}
            queryPlaceholder={t('Section.Edit.Sections.Recommendation.AdditionalFilters.tagQueryPlaceholder')}
            tagBtnCta={t('Section.Edit.Sections.Recommendation.AdditionalFilters.addTagBtnText')}
          />
        )}
        <EnabledUrls
          page={page}
          widgetType={widgetType}
          errors={form.fields.enabledUrls?.error}
          urls={form.fields.enabledUrls?.value}
          onChange={form.fields.enabledUrls?.onChange}
        />
        {widgetType === WIDGETS.COLLECTIONS_WIDGET.value && (
          <CollectionSelection
            collections={form.fields.collections.value}
            onChange={form.fields.collections.onChange}
          />
        )}
        {widgetType === WIDGETS.SLOTS_WIDGET.value && (
          <SlotsRecommendation
            slots={form.fields.slots.value}
            onChange={form.fields.slots.onChange}
            section={section}
            page={sectionPage}
            dashboardData={dashboardData}
            form={form}
          />
        )}
        {widgetType === WIDGETS.PROMOTED_PRODUCTS.value && (
          <ObjectRecommendation
            objects={form.fields.objects.value}
            setObjects={form.fields.objects.onChange}
            error={form.fields.objects.error}
            setError={form.fields.objects.setError}
          />
        )}
        {widgetType === WIDGETS.CUSTOM_WIDGET.value && (
          <Card padding={'0'}>
              <Box padding={'400'}>
                <Text as="h2" variant="headingMd">
                  {t('CustomWidget.title')}
                </Text>
                <Text>{t('CustomWidget.description')}</Text>
              </Box>
              <Divider/>
              <Box padding={'400'}>
                <Text as="h3" variant="headingSm">
                  {t('CustomWidget.RecommendationLogic.title')}
                </Text>
                <Text variant="bodySm" tone="subdued">
                  {t('CustomWidget.RecommendationLogic.description')}
                </Text>
                <BlockStack gap="300">
                  <RadioButton
                    label={t('CustomWidget.RecommendationLogic.SimilarRecentlyViewed.label')}
                    helpText={t('CustomWidget.RecommendationLogic.SimilarRecentlyViewed.helpText')}
                    checked={!form.fields.extra.value?.logic || form.fields.extra.value?.logic === ''}
                    id="similar_recently_viewed"
                    name="recommendationLogic"
                    onChange={() => {
                      form.fields.extra.onChange({
                        ...form.fields.extra.value,
                        logic: ''
                      })
                    }}
                  />

                  <BlockStack gap="200">
                    <RadioButton
                      label={t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.label')}
                      helpText={t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.helpText')}
                      checked={form.fields.extra.value?.logic === CUSTOM_RECOMMENDATION_LOGIC.PERSONALIZED_WITH_PINNED}
                      id={CUSTOM_RECOMMENDATION_LOGIC.PERSONALIZED_WITH_PINNED}
                      name="recommendationLogic"
                      onChange={() => {
                        form.fields.extra.onChange({
                          ...form.fields.extra.value,
                          logic: CUSTOM_RECOMMENDATION_LOGIC.PERSONALIZED_WITH_PINNED
                        })
                      }}
                    />

                    {form.fields.extra.value?.logic === CUSTOM_RECOMMENDATION_LOGIC.PERSONALIZED_WITH_PINNED && (
                      <Box paddingInlineStart="600" paddingBlockStart="200">
                        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                          <BlockStack gap="300">
                            <Banner status="info">
                              <Text>
                                {t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.banner')}
                              </Text>
                            </Banner>

                            <ProductTagList
                              id="personalized-pinned-tags"
                              tagsOnly={true}
                              tagsTitle={t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.additionalPinnedTags.title')}
                              tagsDescription={t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.additionalPinnedTags.description')}
                              tags={form.fields.extra.value?.pinnedTags || []}
                              onTagChange={(tags) => {
                                form.fields.extra.onChange({
                                  ...form.fields.extra.value,
                                  pinnedTags: tags
                                })
                              }}
                              queryPlaceholder={t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.additionalPinnedTags.placeholder')}
                            />

                            <Select
                              label={t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.ordering.label')}
                              options={[
                                { label: t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.ordering.options.personalized'), value: RECOMMENDATIONS_RANKING_CRITERIA.PERSONALISED },
                                { label: t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.ordering.options.recently_launched'), value: RECOMMENDATIONS_RANKING_CRITERIA.NEW_ARRIVALS }
                              ]}
                              value={form.fields.extra.value?.ordering || RECOMMENDATIONS_RANKING_CRITERIA.PERSONALISED}
                              onChange={(value) => {
                                form.fields.extra.onChange({
                                  ...form.fields.extra.value,
                                  ordering: value
                                })
                              }}
                              helpText={t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.ordering.helpText')}
                            />

                            <Text variant="bodyMd">
                              <Text as="strong">{t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.howItWorks.title')}</Text>
                            </Text>
                            <ul style={{ marginLeft: '20px' }}>
                              <li>{t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.howItWorks.step1')}</li>
                              <li>{t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.howItWorks.step2')}</li>
                              <li>{t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.howItWorks.step3')}</li>
                              <li>{t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.howItWorks.step4')}</li>
                              <li>{t('CustomWidget.RecommendationLogic.PersonalizedWithPinned.howItWorks.step5')}</li>
                            </ul>
                          </BlockStack>
                        </Box>
                      </Box>
                    )}
                  </BlockStack>

                  <BlockStack gap="200">
                    <RadioButton
                      label={t('CustomWidget.RecommendationLogic.AlwaysChanging.label')}
                      helpText={t('CustomWidget.RecommendationLogic.AlwaysChanging.helpText')}
                      checked={form.fields.extra.value?.logic === CUSTOM_RECOMMENDATION_LOGIC.ALWAYS_CHANGING_RECENT_TOP_PRODUCTS}
                      id={CUSTOM_RECOMMENDATION_LOGIC.ALWAYS_CHANGING_RECENT_TOP_PRODUCTS}
                      name="recommendationLogic"
                      onChange={() => {
                        form.fields.extra.onChange({
                          ...form.fields.extra.value,
                          logic: CUSTOM_RECOMMENDATION_LOGIC.ALWAYS_CHANGING_RECENT_TOP_PRODUCTS
                        })
                      }}
                    />

                    {form.fields.extra.value?.logic === CUSTOM_RECOMMENDATION_LOGIC.ALWAYS_CHANGING_RECENT_TOP_PRODUCTS && (
                      <Box paddingInlineStart="600" paddingBlockStart="200">
                        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                          <BlockStack gap="300">
                            <Text as="h4" variant="headingSm">
                              {t('CustomWidget.RecommendationLogic.AlwaysChanging.rangeTitle')}
                            </Text>

                            <InlineGrid columns={2} gap="300">
                              <TextField
                                label={t('CustomWidget.RecommendationLogic.AlwaysChanging.startIndex.label')}
                                type="number"
                                value={form.fields.extra.value?.start_index?.toString() || '0'}
                                onChange={(value) => {
                                  form.fields.extra.onChange({
                                    ...form.fields.extra.value,
                                    start_index: parseInt(value) || 0
                                  })
                                }}
                                helpText={t('CustomWidget.RecommendationLogic.AlwaysChanging.startIndex.helpText')}
                                min={0}
                              />

                              <TextField
                                label={t('CustomWidget.RecommendationLogic.AlwaysChanging.endIndex.label')}
                                type="number"
                                value={form.fields.extra.value?.end_index?.toString() || '500'}
                                onChange={(value) => {
                                  form.fields.extra.onChange({
                                    ...form.fields.extra.value,
                                    end_index: parseInt(value) || 500
                                  })
                                }}
                                helpText={t('CustomWidget.RecommendationLogic.AlwaysChanging.endIndex.helpText')}
                                min={1}
                              />
                            </InlineGrid>

                            <Banner status="info">
                              <Text>
                                {t('CustomWidget.RecommendationLogic.AlwaysChanging.banner', {
                                  startIndex: form.fields.extra.value?.start_index || 0,
                                  endIndex: form.fields.extra.value?.end_index || 500
                                })}
                              </Text>
                            </Banner>
                          </BlockStack>
                        </Box>
                      </Box>
                    )}
                  </BlockStack>

                  <RadioButton
                    label={t('CustomWidget.RecommendationLogic.CoupleOfAll.label')}
                    helpText={t('CustomWidget.RecommendationLogic.CoupleOfAll.helpText')}
                    checked={form.fields.extra.value?.logic === CUSTOM_RECOMMENDATION_LOGIC.COUPLE_OF_ALL}
                    id={CUSTOM_RECOMMENDATION_LOGIC.COUPLE_OF_ALL}
                    name="recommendationLogic"
                    onChange={() => {
                      form.fields.extra.onChange({
                        ...form.fields.extra.value,
                        logic: CUSTOM_RECOMMENDATION_LOGIC.COUPLE_OF_ALL
                      })
                    }}
                  />

                  <RadioButton
                    label={t('CustomWidget.RecommendationLogic.ZipcodeRegion.label')}
                    helpText={t('CustomWidget.RecommendationLogic.ZipcodeRegion.helpText')}
                    checked={form.fields.extra.value?.logic === CUSTOM_RECOMMENDATION_LOGIC.ZIPCODE_REGION_TAG_TARGETTING}
                    id={CUSTOM_RECOMMENDATION_LOGIC.ZIPCODE_REGION_TAG_TARGETTING}
                    name="recommendationLogic"
                    onChange={() => {
                      form.fields.extra.onChange({
                        ...form.fields.extra.value,
                        logic: CUSTOM_RECOMMENDATION_LOGIC.ZIPCODE_REGION_TAG_TARGETTING
                      })
                    }}
                  />

                  <BlockStack gap="200">
                    <RadioButton
                      label={t('CustomWidget.RecommendationLogic.CategoryBestseller.label')}
                      helpText={t('CustomWidget.RecommendationLogic.CategoryBestseller.helpText')}
                      checked={form.fields.extra.value?.logic === CUSTOM_RECOMMENDATION_LOGIC.CATEGORY_BESTSELLER}
                      id={CUSTOM_RECOMMENDATION_LOGIC.CATEGORY_BESTSELLER}
                      name="recommendationLogic"
                      onChange={() => {
                        form.fields.extra.onChange({
                          ...form.fields.extra.value,
                          logic: CUSTOM_RECOMMENDATION_LOGIC.CATEGORY_BESTSELLER
                        })
                      }}
                    />

                    {form.fields.extra.value?.logic === CUSTOM_RECOMMENDATION_LOGIC.CATEGORY_BESTSELLER && (
                      <Box paddingInlineStart="600" paddingBlockStart="200">
                        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                          <BlockStack gap="300">
                            <Text as="h4" variant="headingSm">
                              {t('CustomWidget.RecommendationLogic.CategoryBestseller.configTitle')}
                            </Text>

                            <InlineGrid columns={2} gap="300">
                              <TextField
                                label={t('CustomWidget.RecommendationLogic.CategoryBestseller.categoryPrefix.label')}
                                value={form.fields.extra.value?.category_prefix || 'Category_'}
                                onChange={(value) => {
                                  form.fields.extra.onChange({
                                    ...form.fields.extra.value,
                                    category_prefix: value
                                  })
                                }}
                                helpText={t('CustomWidget.RecommendationLogic.CategoryBestseller.categoryPrefix.helpText')}
                                placeholder={t('CustomWidget.RecommendationLogic.CategoryBestseller.categoryPrefix.placeholder')}
                              />

                              <TextField
                                label={t('CustomWidget.RecommendationLogic.CategoryBestseller.subcategoryPrefix.label')}
                                value={form.fields.extra.value?.sub_category_prefix || 'SubCategory_'}
                                onChange={(value) => {
                                  form.fields.extra.onChange({
                                    ...form.fields.extra.value,
                                    sub_category_prefix: value
                                  })
                                }}
                                helpText={t('CustomWidget.RecommendationLogic.CategoryBestseller.subcategoryPrefix.helpText')}
                                placeholder={t('CustomWidget.RecommendationLogic.CategoryBestseller.subcategoryPrefix.placeholder')}
                              />
                            </InlineGrid>
                          </BlockStack>
                        </Box>
                      </Box>
                    )}
                  </BlockStack>

                  <BlockStack gap="200">
                    <RadioButton
                      label={t('CustomWidget.RecommendationLogic.RecentlyUpdated.label')}
                      helpText={t('CustomWidget.RecommendationLogic.RecentlyUpdated.helpText')}
                      checked={form.fields.extra.value?.logic === CUSTOM_RECOMMENDATION_LOGIC.RECENTLY_UPDATED}
                      id={CUSTOM_RECOMMENDATION_LOGIC.RECENTLY_UPDATED}
                      name="recommendationLogic"
                      onChange={() => {
                        form.fields.extra.onChange({
                          ...form.fields.extra.value,
                          logic: CUSTOM_RECOMMENDATION_LOGIC.RECENTLY_UPDATED
                        })
                      }}
                    />

                    {form.fields.extra.value?.logic === CUSTOM_RECOMMENDATION_LOGIC.RECENTLY_UPDATED && (
                      <Box paddingInlineStart="600" paddingBlockStart="200">
                        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                          <BlockStack gap="300">
                            <TextField
                              label={t('CustomWidget.RecommendationLogic.RecentlyUpdated.daysBack.label')}
                              type="number"
                              value={form.fields.extra.value?.days_to_look_back?.toString() || '30'}
                              onChange={(value) => {
                                form.fields.extra.onChange({
                                  ...form.fields.extra.value,
                                  days_to_look_back: parseInt(value) || 30
                                })
                              }}
                              helpText={t('CustomWidget.RecommendationLogic.RecentlyUpdated.daysBack.helpText')}
                              min={1}
                              max={365}
                            />
                          </BlockStack>
                        </Box>
                      </Box>
                    )}
                  </BlockStack>

                  <BlockStack gap="200">
                    <RadioButton
                      label={t('CustomWidget.RecommendationLogic.PersonalizedRecencySalesPaginated.label')}
                      helpText={t('CustomWidget.RecommendationLogic.PersonalizedRecencySalesPaginated.helpText')}
                      checked={form.fields.extra.value?.logic === CUSTOM_RECOMMENDATION_LOGIC.PERSONALIZED_WITH_RECENCY_SALES_PAGINATED}
                      id={CUSTOM_RECOMMENDATION_LOGIC.PERSONALIZED_WITH_RECENCY_SALES_PAGINATED}
                      name="recommendationLogic"
                      onChange={() => {
                        form.fields.extra.onChange({
                          ...form.fields.extra.value,
                          logic: CUSTOM_RECOMMENDATION_LOGIC.PERSONALIZED_WITH_RECENCY_SALES_PAGINATED
                        })
                      }}
                    />
                  </BlockStack>
                </BlockStack>
              </Box>
          </Card>
        )}

      </BlockStack>
      <RecModal
        sectionId={sectionId}
        setting={openedSetting}
        dispatcher={setOpenedSetting}
        widgetType={widgetType}
        currency={dashboardData?.config?.currencyFormat}
      />
      {widgetType === WIDGETS.BOUGHT_TOGETHER.value && (
        <SlotRulesModal
          open={slotRulesModalOpen}
          onClose={() => setSlotRulesModalOpen(false)}
          onSave={(data) => {
            console.log('Slot Rules Data:', data)
            // Save the slot rules data to the form's extra field
            form.fields.extra.onChange({
              ...form.fields.extra.value,
              slotRules: data,
            })
            setSlotRulesModalOpen(false)
          }}
          initialData={form.fields.extra.value.slotRules}
          title="Configure Slot Rules for Frequently Bought Together"
          widgetId={sectionId}
          widgetType="bought_together"
          section={section}
        />
      )}
    </Box>
  )
}

const RecModal = memo(function (props) {
  return <RecommendationSettingsModal {...props} />
})

const ALL_SETTINGS = [
  'manualRecommendation',
  'ruleBasedRecommendation', 
  'slotRules',
  'globalRecommendation'
]

const RecommendationsVisiblMap = {
  manualRecommendation: (widgetType) =>
    [
      WIDGETS.SIMILAR_PRODUCTS.value,
      WIDGETS.CROSS_SELL.value,
      WIDGETS.BOUGHT_TOGETHER.value,
    ].includes(widgetType),
  ruleBasedRecommendation: (widgetType, pageType) =>
    [WIDGETS.BOUGHT_TOGETHER.value, WIDGETS.CROSS_SELL.value].includes(widgetType) &&
    [
      COLLECTION.value,
      PRODUCT.value,
      CART.value,
      ORDER_CONFIRM.value,
      AJAX_CART.value,
      CHECKOUT.value,
    ].includes(pageType),
  slotRules: (widgetType, pageType) =>
    widgetType === WIDGETS.BOUGHT_TOGETHER.value &&
    [PRODUCT.value].includes(pageType),
  globalRecommendation: (widgetType) =>
    [
      WIDGETS.BOUGHT_TOGETHER.value,
      WIDGETS.SIMILAR_PRODUCTS.value,
      WIDGETS.TRENDING.value,
      WIDGETS.PURCHASES.value,
      WIDGETS.BESTSELLERS.value,
      WIDGETS.RECENTLY_LAUNCHED.value,
      WIDGETS.PERSONALIZED.value,
    ].includes(widgetType),
}